
import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { BugReport, BugReportStatus } from "@/hooks/useBugReports";
import { format } from "date-fns";
import { AlertCircle, CheckCircle, Clock, Edit, Loader2, Image as ImageIcon } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { BugReportScreenshots } from "./BugReportScreenshots";

interface BugReportsListProps {
  reports: BugReport[];
  isLoading: boolean;
  onUpdateStatus: (id: string, status: BugReportStatus) => void;
  isUpdating: boolean;
  viewMode: "active" | "completed" | "mine";
  onChangeViewMode: (mode: "active" | "completed" | "mine") => void;
}

export function BugReportsList({ 
  reports, 
  isLoading, 
  onUpdateStatus, 
  isUpdating,
  viewMode,
  onChangeViewMode
}: BugReportsListProps) {
  const [expandedReportId, setExpandedReportId] = useState<string | null>(null);

  const getCategoryBadge = (category: string) => {
    switch (category) {
      case 'Bug':
        return <Badge variant="destructive">{category}</Badge>;
      case 'Feature':
        return <Badge variant="secondary">{category}</Badge>;
      case 'Hotfix':
        return <Badge className="bg-amber-500">{category}</Badge>;
      default:
        return <Badge>{category}</Badge>;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'Open':
        return <Badge variant="outline" className="flex items-center gap-1"><AlertCircle className="h-3 w-3" /> {status}</Badge>;
      case 'In Work':
        return <Badge variant="outline" className="flex items-center gap-1 border-blue-500 text-blue-500"><Clock className="h-3 w-3" /> {status}</Badge>;
      case 'Complete':
        return <Badge variant="outline" className="flex items-center gap-1 border-green-500 text-green-500"><CheckCircle className="h-3 w-3" /> {status}</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const toggleExpand = (id: string) => {
    if (expandedReportId === id) {
      setExpandedReportId(null);
    } else {
      setExpandedReportId(id);
    }
  };

  const getViewModeLabel = () => {
    switch(viewMode) {
      case "active": return "Active Reports";
      case "completed": return "Completed Reports";
      case "mine": return "My Assigned Reports";
      default: return "Bug Reports";
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Bug Reports</CardTitle>
        </CardHeader>
        <CardContent className="text-center py-8">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading bug reports...</p>
        </CardContent>
      </Card>
    );
  }

  const hasScreenshots = (report: BugReport) => {
    return report.screenshots && report.screenshots.length > 0;
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle>{getViewModeLabel()}</CardTitle>
        <div className="flex items-center gap-2">
          <ToggleGroup type="single" value={viewMode} onValueChange={(value) => value && onChangeViewMode(value as "active" | "completed" | "mine")}>
            <ToggleGroupItem value="active" aria-label="Active Reports">
              Active
            </ToggleGroupItem>
            <ToggleGroupItem value="completed" aria-label="Completed Reports">
              Completed
            </ToggleGroupItem>
            <ToggleGroupItem value="mine" aria-label="My Assigned Reports">
              Mine
            </ToggleGroupItem>
          </ToggleGroup>
        </div>
      </CardHeader>
      <CardContent>
        {!reports.length ? (
          <div className="text-center py-8">
            <p className="text-muted-foreground">No bug reports found.</p>
          </div>
        ) : (
          <div className="overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {reports.map((report) => (
                  <React.Fragment key={report.id}>
                    <TableRow className="cursor-pointer" onClick={() => toggleExpand(report.id)}>
                      <TableCell className="font-medium flex items-center">
                        {report.title}
                        {hasScreenshots(report) && (
                          <ImageIcon className="h-4 w-4 ml-2 text-muted-foreground" />
                        )}
                      </TableCell>
                      <TableCell>{getCategoryBadge(report.category)}</TableCell>
                      <TableCell>{report.location}</TableCell>
                      <TableCell>{getStatusBadge(report.status)}</TableCell>
                      <TableCell>{format(report.createdAt, "MMM d, yyyy")}</TableCell>
                      <TableCell>
                        <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); toggleExpand(report.id); }}>
                          <Edit className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                    {expandedReportId === report.id && (
                      <TableRow className="bg-muted/50">
                        <TableCell colSpan={6} className="p-4">
                          <div className="space-y-4">
                            <div>
                              <h4 className="font-semibold mb-2">Description</h4>
                              <p className="text-sm whitespace-pre-wrap">{report.description}</p>
                            </div>
                            
                            {hasScreenshots(report) && (
                              <BugReportScreenshots screenshots={report.screenshots} />
                            )}
                            
                            <div className="flex items-center gap-4">
                              <label className="font-semibold text-sm">Update Status:</label>
                              <Select
                                defaultValue={report.status}
                                onValueChange={(value) => onUpdateStatus(report.id, value as BugReportStatus)}
                                disabled={isUpdating}
                              >
                                <SelectTrigger className="w-36">
                                  <SelectValue placeholder="Select status" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="Open">Open</SelectItem>
                                  <SelectItem value="In Work">In Work</SelectItem>
                                  <SelectItem value="Complete">Complete</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
